import type { Car } from "../providers/vehiclesProvider";
import { Create, EditBase, Resource, Tab, useRedirect } from "react-admin";
import { CarForm } from "../details/vehicles/CarForm";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Tabs } from "../misc/Tabs";
import { VehicleCalendar } from "../details/vehicles/VehicleCalendar";
import { grants } from "../backoffice.access_control";
import { VehicleDetailsHeader } from "../details/vehicles/VehicleDetailsHeader";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { AccessDenied } from "../misc/AccessDenied";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DefaultList } from "../misc/DefaultList";
import { VehicleDataCard } from "../details/vehicles/VehicleDataCard";

function CreateCarForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  useEffect(() => {
    if (!grants.has("Vehicle:create") && drivingSchoolId) {
      redirect(`/drivingSchools/${drivingSchoolId}/fleet#cars`);
    }
  }, []);

  if (!grants.has("Vehicle:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create<Car>
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: ({ name }) => {
          // eslint-disable-next-line react-compiler/react-compiler
          window.onNavigation = () => {
            delete window.onNavigation;
            showSuccessToast(`PKW ${name} erfolgreich gespeichert`);
          };
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#cars`);
        },
      }}
    >
      <CarForm mode="create" sx={{ padding: overviewCardStyle.padding }} />
    </Create>
  );
}

function CarDetails() {
  const redirect = useRedirect();
  const [{ drivingSchoolId }] = useAutovioContext();
  const car = useRecordAndSwitchDrivingSchoolIfNecessary<Car>((it) => it.drivingSchoolId);

  if (!car) {
    return <LoadingIndicator />;
  }

  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert");
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#cars`);
        },
      }}
    >
      <Tabs
        sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "10px 30px" } }}
        header={<VehicleDetailsHeader />}
        mobileSelectAriaLabel="Fahrzeugbereich"
        syncWithLocation={false}
        syncWithHash
        separateCards
      >
        <Tab label="Kalender" value="calendar">
          <VehicleCalendar />
        </Tab>
        {grants.has("Vehicle:edit") && (
          <Tab label="Fahrzeug-Daten" value="data">
            <VehicleDataCard>
              <CarForm mode="edit" />
            </VehicleDataCard>
          </Tab>
        )}
      </Tabs>
    </EditBase>
  );
}

export const carsResource = (
  <Resource
    key="cars"
    name="cars"
    list={DefaultList}
    create={CreateCarForm}
    edit={CarDetails}
    options={{ label: "PKWs", createTitle: "Neuer PKW" }}
    recordRepresentation={(record: Car) => `${record.name} (${record.plateNumber})`}
  />
);
